import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query"
import { useAuthStore } from "@/lib/store/authStore"
import { toast } from "sonner"
import { gbpApi } from "./gbpApi"
import type {
  GBPPeriod,
  GBPReviewsResponse,
  GBPPhotosResponse,
} from "./gbpTypes"

function useTenant() {
  return useAuthStore((s) => s.currentTenant)
}

export const gbpKeys = {
  all: (tenant: string | null) => ["gbp", tenant] as const,
  connection: (tenant: string | null) =>
    ["gbp", "connection", tenant] as const,
  locations: (tenant: string | null) =>
    ["gbp", "locations", tenant] as const,
  categories: (tenant: string | null, query: string) =>
    ["gbp", "categories", tenant, query] as const,
  prefill: (tenant: string | null) => ["gbp", "prefill", tenant] as const,
  verification: (tenant: string | null) =>
    ["gbp", "verification", tenant] as const,
  performance: (tenant: string | null, period: GBPPeriod) =>
    ["gbp", "performance", tenant, period] as const,
  keywords: (tenant: string | null, period: GBPPeriod) =>
    ["gbp", "keywords", tenant, period] as const,
  reviews: (tenant: string | null) => ["gbp", "reviews", tenant] as const,
  reviewBadge: (tenant: string | null) =>
    ["gbp", "reviewBadge", tenant] as const,
  photos: (tenant: string | null) => ["gbp", "photos", tenant] as const,
  businessInfo: (tenant: string | null) =>
    ["gbp", "businessInfo", tenant] as const,
  dashboardSummary: (tenant: string | null) =>
    ["gbp", "dashboardSummary", tenant] as const,
}

// ---- Connection (Epic 01) ----

export function useGBPConnection() {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.connection(tenant),
    queryFn: () => gbpApi.getConnectionStatus(),
    enabled: !!tenant,
  })
}

export function useGBPLocations(enabled: boolean) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.locations(tenant),
    queryFn: () => gbpApi.listLocations(),
    enabled: !!tenant && enabled,
  })
}

export function useGBPCategorySearch(query: string) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.categories(tenant, query),
    queryFn: () => gbpApi.searchCategories(query),
    enabled: !!tenant && query.length >= 2,
  })
}

export function useCompanyPrefill() {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.prefill(tenant),
    queryFn: () => gbpApi.getCompanyPrefill(),
    enabled: !!tenant,
  })
}

export function useSelectLocation() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: ({
      locationId,
      accountId,
    }: {
      locationId: string
      accountId: string
    }) => gbpApi.selectLocation(locationId, accountId),
    onSuccess: () => {
      toast.success("เลือกสถานที่สำเร็จ")
      queryClient.invalidateQueries({
        queryKey: gbpKeys.connection(tenant),
      })
    },
    onError: () => {
      toast.error("ไม่สามารถเลือกสถานที่ได้ กรุณาลองใหม่")
    },
  })
}

export function useCreateListing() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: gbpApi.createListing,
    onSuccess: () => {
      toast.success("สร้างข้อมูลธุรกิจบน Google สำเร็จ")
      queryClient.invalidateQueries({
        queryKey: gbpKeys.connection(tenant),
      })
    },
    onError: () => {
      toast.error("ไม่สามารถสร้างข้อมูลธุรกิจได้ กรุณาลองใหม่")
    },
  })
}

// ---- Verification (Epic 02) ----

export function useGBPVerificationOptions(enabled: boolean) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.verification(tenant),
    queryFn: () => gbpApi.getVerificationOptions(),
    enabled: !!tenant && enabled,
  })
}

export function useRequestVerification() {
  return useMutation({
    mutationFn: ({
      method,
      phoneNumber,
      emailAddress,
    }: {
      method: string
      phoneNumber?: string
      emailAddress?: string
    }) => gbpApi.requestVerification(method, phoneNumber, emailAddress),
    onError: () => {
      toast.error("ไม่สามารถส่งรหัสยืนยันได้ กรุณาลองใหม่")
    },
  })
}

export function useCompleteVerification() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: (pin: string) => gbpApi.completeVerification(pin),
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: gbpKeys.connection(tenant),
        })
        queryClient.invalidateQueries({
          queryKey: gbpKeys.verification(tenant),
        })
      }
    },
    onError: () => {
      toast.error("การยืนยันล้มเหลว กรุณาลองใหม่")
    },
  })
}

// ---- Insights (Epic 02) ----

export function useGBPPerformance(period: GBPPeriod) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.performance(tenant, period),
    queryFn: () => gbpApi.getPerformanceMetrics(period),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  })
}

export function useGBPSearchKeywords(period: GBPPeriod) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.keywords(tenant, period),
    queryFn: () => gbpApi.getSearchKeywords(period),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  })
}

// ---- Reviews (Epic 03) ----

export function useGBPReviews(enabled = true) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.reviews(tenant),
    queryFn: () => gbpApi.getReviews(5),
    enabled: !!tenant && enabled,
    refetchInterval: 300000, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

export function useGBPReviewBadge() {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.reviewBadge(tenant),
    queryFn: () => gbpApi.getReviewBadgeCount(),
    enabled: !!tenant,
    refetchInterval: 300000, // 5 minutes
    placeholderData: keepPreviousData,
  })
}

// ---- Photos (Epic 04) ----

export function useGBPPhotos(enabled = true) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.photos(tenant),
    queryFn: () => gbpApi.getPhotos(),
    enabled: !!tenant && enabled,
    staleTime: 3_600_000, // 1 hour
    placeholderData: keepPreviousData,
  })
}

export function useUploadPhoto() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: (file: File) => gbpApi.uploadPhoto(file),
    onSuccess: () => {
      toast.success("อัพโหลดรูปภาพสำเร็จ")
      queryClient.invalidateQueries({ queryKey: gbpKeys.photos(tenant) })
    },
    onError: () => {
      toast.error("ไม่สามารถอัพโหลดรูปภาพได้ กรุณาลองใหม่")
    },
  })
}

export function useDeletePhoto() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: (photoId: string) => gbpApi.deletePhoto(photoId),
    onMutate: async (photoId) => {
      await queryClient.cancelQueries({ queryKey: gbpKeys.photos(tenant) })
      const prev = queryClient.getQueryData<GBPPhotosResponse>(
        gbpKeys.photos(tenant),
      )
      if (prev) {
        queryClient.setQueryData<GBPPhotosResponse>(gbpKeys.photos(tenant), {
          ...prev,
          photos: prev.photos.filter((p) => p.photo_id !== photoId),
          total_count: Math.max(0, prev.total_count - 1),
        })
      }
      return { prev }
    },
    onError: (_err, _photoId, context) => {
      if (context?.prev) {
        queryClient.setQueryData(gbpKeys.photos(tenant), context.prev)
      }
      toast.error("ไม่สามารถลบรูปภาพได้ กรุณาลองใหม่")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: gbpKeys.photos(tenant) })
    },
  })
}

// ---- Business Info (Epic 04) ----

export function useGBPBusinessInfo(enabled = true) {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.businessInfo(tenant),
    queryFn: () => gbpApi.getLocationInfo(),
    enabled: !!tenant && enabled,
    staleTime: 3_600_000, // 1 hour
    placeholderData: keepPreviousData,
  })
}

// ---- Dashboard Summary (Epic 04) ----

export function useGBPDashboardSummary() {
  const tenant = useTenant()
  return useQuery({
    queryKey: gbpKeys.dashboardSummary(tenant),
    queryFn: () => gbpApi.getDashboardSummary(),
    enabled: !!tenant,
    staleTime: 21_600_000, // 6 hours
    placeholderData: keepPreviousData,
  })
}

// ---- Disconnect (Epic 04) ----

export function useDisconnectGBP() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: () => gbpApi.disconnect(),
    onSuccess: () => {
      toast.success("ยกเลิกการเชื่อมต่อ Google Business สำเร็จ")
      // Invalidate ALL GBP queries
      queryClient.invalidateQueries({ queryKey: gbpKeys.all(tenant) })
    },
    onError: () => {
      toast.error("ไม่สามารถยกเลิกการเชื่อมต่อได้ กรุณาลองใหม่")
    },
  })
}

// ---- Reviews (Epic 03) ----

export function useReplyToReview() {
  const queryClient = useQueryClient()
  const tenant = useTenant()
  return useMutation({
    mutationFn: ({ reviewId, text }: { reviewId: string; text: string }) =>
      gbpApi.replyToReview(reviewId, text),
    onMutate: async ({ reviewId, text }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: gbpKeys.reviews(tenant) })
      await queryClient.cancelQueries({
        queryKey: gbpKeys.reviewBadge(tenant),
      })

      // Snapshot previous values
      const prevReviews = queryClient.getQueryData<GBPReviewsResponse>(
        gbpKeys.reviews(tenant),
      )
      const prevBadge = queryClient.getQueryData<{ count: number }>(
        gbpKeys.reviewBadge(tenant),
      )

      // Optimistic update: add reply to review, decrement unreplied count
      if (prevReviews) {
        queryClient.setQueryData<GBPReviewsResponse>(
          gbpKeys.reviews(tenant),
          {
            ...prevReviews,
            reviews: prevReviews.reviews.map((r) =>
              r.review_id === reviewId ? { ...r, reply_text: text } : r,
            ),
            unreplied_count: Math.max(0, prevReviews.unreplied_count - 1),
          },
        )
      }
      if (prevBadge) {
        queryClient.setQueryData(gbpKeys.reviewBadge(tenant), {
          count: Math.max(0, prevBadge.count - 1),
        })
      }

      return { prevReviews, prevBadge }
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.prevReviews) {
        queryClient.setQueryData(gbpKeys.reviews(tenant), context.prevReviews)
      }
      if (context?.prevBadge) {
        queryClient.setQueryData(
          gbpKeys.reviewBadge(tenant),
          context.prevBadge,
        )
      }
      toast.error("ไม่สามารถตอบรีวิวได้ กรุณาลองใหม่")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: gbpKeys.reviews(tenant) })
      queryClient.invalidateQueries({
        queryKey: gbpKeys.reviewBadge(tenant),
      })
    },
  })
}
